import { useUserAccount } from '@/api/user/queries';
import { ModalsKey } from '@/enums/modalsKey';
import { useModalNavigation } from '@/hooks/modals/useModalNavigation';
import { usePathname } from '@/hooks/usePathname';
import { useSearchParams } from '@starsoft/common/hooks';
import useTranslation from 'next-translate/useTranslation';
import NextLink from 'next/link';
import { useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { CoreLinkProps } from './props';

export default function Link({
  as,
  children,
  href,
  replace,
  scroll,
  shallow,
  modal,
  privateRoute = false,
  ...rest
}: CoreLinkProps) {
  const { lang } = useTranslation('common');
  const { getModalLink } = useModalNavigation();
  const { isAuthorized } = useUserAccount();
  const searchParams = useSearchParams();
  const pathname: string = usePathname();

  function getHref() {
    if (privateRoute && !isAuthorized) {
      return `${pathname}#${ModalsKey.Login}`;
    }

    if (modal) {
      return getModalLink(modal, href);
    }

    return `${String(href).startsWith('/') ? href : `/${href}`}`;
  }

  const shouldPrefetch = !modal && !privateRoute;
  const finalHref = getHref();

  const preloadData = useCallback(async () => {
    if (!shouldPrefetch || typeof finalHref !== 'string') return;

    try {
      // Convert the href to the _next/data URL format
      const buildId = window?.__NEXT_DATA__?.buildId;
      const locale = window?.__NEXT_DATA__?.locale || lang;
      const cleanPath = finalHref?.replace?.(/^\//, '')?.replace?.(/\/$/, '');
      const dataUrl = `/_next/data/${buildId}/${locale}${cleanPath ? `/${cleanPath}` : ''}.json${searchParams?.size > 0 ? `?${searchParams?.toString()}` : ''}`;

      // Fetch with caching options
      await fetch(dataUrl, {
        headers: {
          'Cache-Control':
            'public, max-age=30, s-maxage=60, stale-while-revalidate=300',
        },
        next: {
          revalidate: 30,
          tags: [`page-data-${dataUrl}`],
        },
      });
    } catch (error) {
      // Silently handle errors since this is just preloading
      console.debug('Preload failed:', error);
    }
  }, [finalHref, shouldPrefetch, lang, searchParams]);

  const { ref } = useInView({
    triggerOnce: true,
    onChange: async inView => {
      if (!inView) {
        return;
      }

      await preloadData();
    },
  });

  return (
    //@ts-ignore
    <NextLink
      {...rest}
      as={as}
      href={finalHref}
      lang={lang}
      passHref
      replace={replace}
      scroll={!!modal ? false : scroll}
      aria-disabled={rest?.disabled}
      shallow={!!modal ? true : shallow}
      aria-label={`Link to ${typeof href == 'object' ? JSON.stringify(href) : href}`}
      prefetch={shouldPrefetch}
      onMouseEnter={preloadData}
      onTouchStart={preloadData}
      ref={ref}
    >
      {children}
    </NextLink>
  );
}
